import {Injectable} from '@angular/core';
import * as moment from 'moment';

@Injectable()
export class LocalStorageService {

  private readonly TIME: moment.DurationInputArg1 = 24;
  private readonly UNITS: moment.unitOfTime.DurationConstructor = 'h';

  constructor() {
  }

  public setItem(key: string, data: any): void {
    localStorage.setItem(key, JSON.stringify(data));
    // Store a timestamp for this specific key to enable per-key expiration
    localStorage.setItem(key + '_TIMESTAMP', moment().toISOString());
    // Also maintain global SET_DATE for backward compatibility
    localStorage.setItem('SET_DATE', moment().toISOString());
  }

  public getItem(key: string): any {
    // Check if this specific key has expired before retrieving
    if (this.isKeyExpired(key)) {
      this.removeItem(key);
      this.removeItem(key + '_TIMESTAMP');
      return null;
    }
    
    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }
    try {
      return JSON.parse(item);
    } catch (e) {
      // If parsing fails, return null
      return null;
    }
  }
  
  private isKeyExpired(key: string): boolean {
    const timestampKey = key + '_TIMESTAMP';
    const timestamp = localStorage.getItem(timestampKey);
    if (!timestamp) {
      // If no timestamp exists, check the global SET_DATE for backward compatibility
      const globalDate = localStorage.getItem('SET_DATE');
      if (globalDate) {
        return moment(globalDate).isBefore(moment().subtract(this.TIME, this.UNITS));
      }
      return false; // No timestamp means it's not expired (might be old data without timestamp)
    }
    return moment(timestamp).isBefore(moment().subtract(this.TIME, this.UNITS));
  }

  public checkValidity(keys: Array<string>, fullClear?: boolean): boolean {
    if (this.hasItem('SET_DATE')) {
      if (moment(localStorage.getItem('SET_DATE')).isBefore(moment().subtract(this.TIME, this.UNITS))) {
        if (keys && keys.length > 0) {
          for (const key of keys) {
            this.removeItem(key);
          }
          this.setItem('SET_DATE', moment().toISOString());
          return true;
        } else {
          if (fullClear) {
            this.clear();
            this.setItem('SET_DATE', moment().toISOString());
            return true;
          }
        }
      }
    }

    return false;
  }

  public hasItem(key: string): Boolean {
    return !!localStorage.getItem(key);
  }

  public removeItem(key: string): void {
    localStorage.removeItem(key);
    localStorage.removeItem(key + '_TIMESTAMP');
  }

  public clear() {
    localStorage.clear();
  }
}

